import { useState, useEffect } from 'react';
import { exchangeAPI } from '../lib/api';
import {
  FiSearch, FiX, FiLoader, FiMail, FiCheck, FiUser,
  FiAlertCircle, FiTrash2
} from 'react-icons/fi';

/**
 * ExchangeMailboxBrowser
 *
 * Manages mailbox selection for an Exchange connection.
 * - Shows currently connected mailboxes at the top with inline remove
 * - Search & select from the full tenant user list
 * - Save persists the entire selection back to the backend
 */
export default function ExchangeMailboxBrowser({ connection, onComplete, onCancel }) {
  const [mailboxes, setMailboxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [selectedMailboxes, setSelectedMailboxes] = useState(
    () => connection?.connected_mailboxes || []
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadMailboxes('');
  }, [connection.id]);

  const loadMailboxes = async (search) => {
    try {
      setLoading(true);
      setError(null);
      const response = await exchangeAPI.getMailboxes(connection.id, search);
      setMailboxes(response.data.mailboxes || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
      setSearching(false);
    }
  };

  const handleSearch = () => {
    setSearching(true);
    loadMailboxes(searchQuery);
  };

  const toggleMailbox = (mailbox) => {
    setSelectedMailboxes(prev => {
      const exists = prev.find(m => m.user_id === mailbox.id);
      if (exists) {
        return prev.filter(m => m.user_id !== mailbox.id);
      }
      return [...prev, {
        user_id: mailbox.id,
        email: mailbox.email,
        display_name: mailbox.displayName,
      }];
    });
  };

  const removeMailbox = (userId) => {
    setSelectedMailboxes(prev => prev.filter(m => m.user_id !== userId));
  };

  const isSelected = (mailbox) => selectedMailboxes.some(m => m.user_id === mailbox.id);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      await exchangeAPI.saveMailboxes(connection.id, selectedMailboxes);
      onComplete();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSelectAll = () => {
    const allSelected = mailboxes.every(mb => isSelected(mb));
    if (allSelected) {
      // Deselect only the currently visible mailboxes
      const visibleIds = new Set(mailboxes.map(mb => mb.id));
      setSelectedMailboxes(prev => prev.filter(m => !visibleIds.has(m.user_id)));
    } else {
      const newEntries = mailboxes
        .filter(mb => !isSelected(mb))
        .map(mb => ({ user_id: mb.id, email: mb.email, display_name: mb.displayName }));
      setSelectedMailboxes(prev => [...prev, ...newEntries]);
    }
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <FiMail className="w-5 h-5 text-purple-600" />
            Manage Mailboxes
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            {connection.tenant_name} — choose which mailboxes are searched
          </p>
        </div>
        <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 transition-colors">
          <FiX className="w-6 h-6" />
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
          <FiAlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* ── Currently selected mailboxes ─────────────────────────────────── */}
      {selectedMailboxes.length > 0 && (
        <div className="border border-purple-200 rounded-lg overflow-hidden">
          <div className="bg-purple-50 px-4 py-2.5 border-b border-purple-200 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-purple-800 flex items-center gap-1.5">
              <FiCheck className="w-4 h-4" />
              Selected ({selectedMailboxes.length} mailbox{selectedMailboxes.length !== 1 ? 'es' : ''})
            </h3>
            <button
              onClick={() => setSelectedMailboxes([])}
              className="text-xs text-purple-600 hover:text-purple-800 transition-colors"
            >
              Clear all
            </button>
          </div>
          <div className="max-h-36 overflow-y-auto divide-y divide-purple-50">
            {selectedMailboxes.map(mb => (
              <div key={mb.user_id} className="flex items-center justify-between px-4 py-2 group">
                <span className="flex items-center gap-2 min-w-0">
                  <FiMail className="w-3.5 h-3.5 text-purple-400 flex-shrink-0" />
                  <span className="text-sm text-gray-800 truncate">{mb.display_name || mb.email}</span>
                  <span className="text-xs text-gray-400 truncate hidden sm:block">{mb.email}</span>
                </span>
                <button
                  onClick={() => removeMailbox(mb.user_id)}
                  className="opacity-0 group-hover:opacity-100 p-1 text-red-400 hover:text-red-600 transition-opacity flex-shrink-0"
                  title="Remove mailbox"
                >
                  <FiTrash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Search ───────────────────────────────────────────────────────── */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            placeholder="Search by name or email..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
          />
        </div>
        <button
          onClick={handleSearch}
          disabled={searching || loading}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors text-sm"
        >
          {searching ? <FiLoader className="w-4 h-4 animate-spin" /> : 'Search'}
        </button>
      </div>

      {/* ── Mailbox list ─────────────────────────────────────────────────── */}
      <div className="border border-gray-200 rounded-lg max-h-80 overflow-y-auto">
        {loading ? (
          <div className="flex justify-center items-center py-12 gap-2">
            <FiLoader className="w-6 h-6 animate-spin text-purple-600" />
            <span className="text-sm text-gray-500">Loading mailboxes…</span>
          </div>
        ) : mailboxes.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <FiUser className="w-8 h-8 mx-auto mb-2 text-gray-400" />
            <p className="text-sm">No mailboxes found</p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {/* Select All */}
            {mailboxes.length > 1 && (
              <li
                className="flex items-center px-4 py-3 cursor-pointer hover:bg-gray-100 bg-gray-50 border-b border-gray-200"
                onClick={handleSelectAll}
              >
                <div className={`w-5 h-5 rounded border-2 mr-3 flex items-center justify-center flex-shrink-0 transition-colors ${
                  mailboxes.every(mb => isSelected(mb))
                    ? 'bg-purple-600 border-purple-600'
                    : mailboxes.some(mb => isSelected(mb))
                      ? 'bg-purple-300 border-purple-300'
                      : 'border-gray-300'
                }`}>
                  {mailboxes.some(mb => isSelected(mb)) && <FiCheck className="w-3 h-3 text-white" />}
                </div>
                <span className="text-sm font-medium text-gray-700">
                  Select All ({mailboxes.length} mailboxes)
                </span>
              </li>
            )}
            {mailboxes.map(mailbox => (
              <li
                key={mailbox.id}
                onClick={() => toggleMailbox(mailbox)}
                className={`flex items-center px-4 py-3 cursor-pointer transition-colors ${
                  isSelected(mailbox) ? 'bg-purple-50 hover:bg-purple-100' : 'hover:bg-gray-50'
                }`}
              >
                <div className={`w-5 h-5 rounded border-2 mr-3 flex items-center justify-center flex-shrink-0 transition-colors ${
                  isSelected(mailbox) ? 'bg-purple-600 border-purple-600' : 'border-gray-300'
                }`}>
                  {isSelected(mailbox) && <FiCheck className="w-3 h-3 text-white" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{mailbox.displayName}</p>
                  <p className="text-xs text-gray-400 truncate">
                    {mailbox.email}
                    {mailbox.jobTitle && ` · ${mailbox.jobTitle}`}
                    {mailbox.department && ` (${mailbox.department})`}
                  </p>
                </div>
                <FiMail className="w-4 h-4 text-gray-300 flex-shrink-0 ml-2" />
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ── Actions ──────────────────────────────────────────────────────── */}
      <div className="flex justify-between items-center pt-2 border-t border-gray-200">
        <button
          onClick={onCancel}
          disabled={saving}
          className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors text-sm"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
        >
          {saving ? (
            <><FiLoader className="w-4 h-4 mr-2 animate-spin" />Saving…</>
          ) : (
            <><FiCheck className="w-4 h-4 mr-2" />Save ({selectedMailboxes.length} mailbox{selectedMailboxes.length !== 1 ? 'es' : ''})</>
          )}
        </button>
      </div>
    </div>
  );
}
